import React from 'react';
import { Check, ArrowRight, KeyRound, Sparkles } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { useLanguage } from '../../lib/LanguageContext';

const PackageCard = ({ pkg, testIdSuffix, Icon, t }) => {
  const handleScroll = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <article
      data-testid={`service-card-${testIdSuffix}`}
      className="group relative flex flex-col h-full bg-[#FBF9F5] border border-sand-200 rounded-sm p-8 md:p-10 hover:border-sand-400 transition-colors"
    >
      <div className="mb-8">
        <div className="w-16 h-16 rounded-full border border-sand-300 bg-cream flex items-center justify-center mb-6">
          <Icon className="w-7 h-7 text-sand-600" strokeWidth={1.2} />
        </div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-terracotta-500 mb-3">{pkg.tag}</div>
        <h3 className="font-serif text-2xl md:text-[28px] text-ink-900 leading-tight">{pkg.title}</h3>
        <div className="text-sm text-ink-500 mt-2">{pkg.subtitle}</div>
      </div>

      <p className="text-[15px] text-ink-700 leading-relaxed mb-6">
        {pkg.description}
      </p>

      <ul className="space-y-3 pt-6 border-t border-sand-200 mb-8">
        {pkg.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] text-ink-700">
            <Check className="w-4 h-4 mt-1 text-olive-500 shrink-0" strokeWidth={1.6} />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-2">
        <button
          data-testid={`service-cta-${testIdSuffix}`}
          onClick={handleScroll}
          className="w-full min-h-[48px] inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-ink-900 text-ink-900 rounded-sm text-sm tracking-wide hover:bg-ink-900 hover:text-cream transition-colors"
        >
          {t.services.cta}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </article>
  );
};

export const Services = () => {
  const { t } = useLanguage();
  return (
    <section id="services" data-testid="services-section" className="pt-16 md:pt-20 pb-10 md:pb-12 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="text-xs uppercase tracking-[0.24em] text-sand-600 mb-6">{t.services.eyebrow}</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink-900 leading-[1.1] mb-6">
            {t.services.title}
          </h2>
          <p className="text-base md:text-lg text-ink-500 leading-relaxed">{t.services.intro}</p>
        </div>

        {/* Desktop: 2 columns side by side */}
        <div className="hidden md:grid md:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          <PackageCard pkg={t.services.a} testIdSuffix="a" Icon={KeyRound} t={t} />
          <PackageCard pkg={t.services.b} testIdSuffix="b" Icon={Sparkles} t={t} />
        </div>

        {/* Mobile: Tabs */}
        <div className="md:hidden">
          <Tabs defaultValue="a" className="w-full">
            <TabsList
              data-testid="services-mobile-tabs"
              className="grid w-full grid-cols-2 h-12 bg-sand-100 border border-sand-200 rounded-sm p-1 mb-6"
            >
              <TabsTrigger
                value="a"
                data-testid="services-tab-a"
                className="min-h-[40px] text-xs tracking-wide data-[state=active]:bg-cream data-[state=active]:text-ink-900 data-[state=active]:shadow-sm text-ink-500"
              >
                {t.services.a.tag}
              </TabsTrigger>
              <TabsTrigger
                value="b"
                data-testid="services-tab-b"
                className="min-h-[40px] text-xs tracking-wide data-[state=active]:bg-cream data-[state=active]:text-ink-900 data-[state=active]:shadow-sm text-ink-500"
              >
                {t.services.b.tag}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="a" className="mt-0">
              <PackageCard pkg={t.services.a} testIdSuffix="a-mobile" Icon={KeyRound} t={t} />
            </TabsContent>
            <TabsContent value="b" className="mt-0">
              <PackageCard pkg={t.services.b} testIdSuffix="b-mobile" Icon={Sparkles} t={t} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default Services;
